import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { facilityAPI } from '../services/api';
import {
  FlaskConical, Loader, ChevronDown, ChevronUp, ExternalLink,
  CheckCircle, XCircle, AlertTriangle, Trash2, ClipboardList,
  ArrowRight, Building2
} from 'lucide-react';
import { toast } from 'react-toastify';

const POLLUTANTS = ['NOx', 'CO', 'VOC', 'PM', 'PM10', 'PM2.5', 'SO2', 'CO2', 'HAP', 'H2S', 'Ammonia', 'Lead'];

const SOURCE_TYPES = [
  'Natural Gas Boiler', 'Distillate Oil Boiler', 'Coal Boiler', 'Biomass Boiler',
  'Natural Gas Combustion Turbine', 'Combined Cycle Gas Turbine (CCGT)',
  'Natural Gas Engine - Lean Burn', 'Natural Gas Engine - Rich Burn',
  'Diesel Engine / Emergency Generator',
  'Portland Cement Kiln', 'Lime Kiln', 'Glass Furnace',
  'Electric Arc Furnace (EAF)', 'Basic Oxygen Furnace (BOF)',
  'Fluid Catalytic Cracking Unit (FCCU)', 'Sulfur Recovery Unit (SRU)',
  'Asphalt Plant', 'Aggregate Dryer',
  'Industrial Boiler - Coal', 'Industrial Boiler - Oil',
  'Flare', 'Thermal Oxidizer', 'Regenerative Thermal Oxidizer (RTO)',
  'Paint Spray Booth', 'Surface Coating Line',
  'Pharmaceutical Manufacturing', 'Chemical Reactor',
  'Wastewater Treatment - Anaerobic', 'Landfill Gas Flare',
  'Other (describe below)'
];

const STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

const ATTAINMENT_OPTIONS = [
  { value: 'Attainment', label: 'Attainment / PSD Area (BACT required)' },
  { value: 'Non-attainment', label: 'Non-Attainment Area (LAER required)' },
  { value: 'Unknown', label: 'Unknown / Need to determine' },
];

function Section({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
      <div onClick={() => setOpen(!open)}
        style={{ padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>
          {icon}{title}
        </div>
        {open ? <ChevronUp size={15} color="#94a3b8" /> : <ChevronDown size={15} color="#94a3b8" />}
      </div>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

function StepBadge({ step, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#0066FF', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{step}</div>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>{label}</div>
    </div>
  );
}

function AnalysisResults({ analysis, recordId, onDelete }) {
  const inputStyle = { fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' };

  return (
    <div>
      {/* Header */}
      <div style={{ background: '#0A1628', borderRadius: 12, padding: '18px 22px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 800, color: 'white', letterSpacing: -0.5 }}>
              {analysis.analysis_type} Analysis
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>
              {analysis.source_type} · {analysis.pollutant} · {analysis.state}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ background: analysis.analysis_type === 'LAER' ? '#FEF2F2' : '#EFF6FF', color: analysis.analysis_type === 'LAER' ? '#DC2626' : '#0066FF', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>
              {analysis.analysis_type}
            </span>
            {recordId && (
              <button onClick={onDelete} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Applicability box */}
        {analysis.applicability && (
          <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Applicability</div>
            <div style={{ fontSize: 13, color: 'white', marginBottom: 4 }}>{analysis.applicability.basis}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              Threshold: {analysis.applicability.threshold} · {analysis.applicability.regulatory_basis}
            </div>
          </div>
        )}
      </div>

      {/* Step 1 - Available Technologies */}
      <Section title="Step 1 — Available Control Technologies" icon={<ClipboardList size={15} color="#0066FF" />}>
        <StepBadge step="1" label="Identify all available control options — most to least stringent" />
        {(analysis.step1_available_technologies || []).map((tech, i) => (
          <div key={i} style={{ background: i === 0 ? '#F0FDF4' : '#F8FAFC', border: `1px solid ${i === 0 ? '#BBF7D0' : '#E2E8F0'}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#16A34A' : '#94a3b8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{tech.rank}</div>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#0A1628' }}>{tech.technology}</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, background: tech.commercial_availability?.includes('Commercially') ? '#EFF6FF' : '#FFFBEB', color: tech.commercial_availability?.includes('Commercially') ? '#1E40AF' : '#92400E', padding: '2px 8px', borderRadius: 8 }}>
                {tech.commercial_availability}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>{tech.description}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
              <div><span style={{ color: '#94a3b8' }}>Efficiency: </span><strong>{tech.typical_efficiency}</strong></div>
              <div><span style={{ color: '#94a3b8' }}>Typical Limit: </span><strong>{tech.typical_limit}</strong></div>
            </div>
            {tech.rblc_examples && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>RBLC: {tech.rblc_examples}</div>
            )}
          </div>
        ))}
      </Section>

      {/* Step 2 - Technical Feasibility */}
      <Section title="Step 2 — Technical Feasibility Screening" icon={<CheckCircle size={15} color="#16A34A" />}>
        <StepBadge step="2" label="Eliminate technically infeasible options" />
        <div style={{ display: 'grid', gap: 8 }}>
          {(analysis.step2_technical_feasibility || []).map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
              {item.feasible
                ? <CheckCircle size={16} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
                : <XCircle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: 2 }} />}
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#0A1628', marginBottom: 2 }}>{item.technology}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{item.reason}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Step 3 - Ranking */}
      <Section title="Step 3 — Rank by Effectiveness" icon={<ArrowRight size={15} color="#7C3AED" />}>
        <StepBadge step="3" label="Rank feasible technologies from most to least stringent" />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Rank</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Technology</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Emission Limit</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {(analysis.step3_ranking || []).map((item, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F1F5F9', background: i === 0 ? '#F0FDF4' : 'white' }}>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#16A34A' : '#E2E8F0', color: i === 0 ? 'white' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{item.rank}</div>
                </td>
                <td style={{ padding: '10px 12px', fontWeight: i === 0 ? 700 : 500, color: '#0A1628' }}>{item.technology}</td>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#0066FF', fontWeight: 600 }}>{item.emission_limit} {item.unit}</td>
                <td style={{ padding: '10px 12px', color: '#64748b' }}>{item.efficiency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Step 4 - Impacts */}
      {analysis.step4_impacts && (
        <Section title="Step 4 — Economic, Energy & Environmental Impacts" icon={<AlertTriangle size={15} color="#D97706" />}>
          <StepBadge step="4" label="Evaluate impacts of top-ranked technology" />
          <div style={{ background: '#0A1628', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Top-Ranked Technology</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>{analysis.step4_impacts.top_technology}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 12 }}>
            {analysis.step4_impacts.economic && (
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0066FF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Economic</div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}><strong>Capital:</strong> {analysis.step4_impacts.economic.capital_cost}</div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}><strong>Annual:</strong> {analysis.step4_impacts.economic.annual_cost}</div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}><strong>Cost-eff:</strong> {analysis.step4_impacts.economic.cost_effectiveness}</div>
                <div style={{ fontSize: 11, color: '#16A34A', fontWeight: 600 }}>{analysis.step4_impacts.economic.economic_feasibility}</div>
              </div>
            )}
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#D97706', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Energy</div>
              <div style={{ fontSize: 12, color: '#374151' }}>{analysis.step4_impacts.energy}</div>
            </div>
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#16A34A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Environmental</div>
              <div style={{ fontSize: 12, color: '#374151' }}>{analysis.step4_impacts.environmental}</div>
            </div>
          </div>
        </Section>
      )}

      {/* Step 5 - BACT Determination */}
      {analysis.step5_bact_determination && (
        <Section title={`Step 5 — ${analysis.analysis_type} Determination`} icon={<CheckCircle size={15} color="#16A34A" />}>
          <StepBadge step="5" label={`Select ${analysis.analysis_type} — the final recommended determination`} />
          <div style={{ background: '#F0FDF4', border: '2px solid #16A34A', borderRadius: 10, padding: 18, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Recommended {analysis.analysis_type}</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 800, color: '#0A1628', marginBottom: 4 }}>
              {analysis.step5_bact_determination.recommended_bact}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 16, color: '#0066FF', fontWeight: 700, marginBottom: 10 }}>
              {analysis.step5_bact_determination.emission_limit} {analysis.step5_bact_determination.limit_unit}
              {analysis.step5_bact_determination.averaging_period && ` (${analysis.step5_bact_determination.averaging_period})`}
            </div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.65 }}>{analysis.step5_bact_determination.rationale}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {analysis.step5_bact_determination.monitoring && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Monitoring Required</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{analysis.step5_bact_determination.monitoring}</div>
              </div>
            )}
            {analysis.step5_bact_determination.recordkeeping && (
              <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#5B21B6', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Recordkeeping</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{analysis.step5_bact_determination.recordkeeping}</div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Recent RBLC Trends */}
      {analysis.recent_rblc_trends && analysis.recent_rblc_trends.length > 0 && (
        <Section title="Recent RBLC Trends" icon={<Building2 size={15} color="#64748b" />} defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Recent comparable determinations from the RBLC database — verify directly at EPA.gov</div>
          {analysis.recent_rblc_trends.map((trend, i) => (
            <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#0A1628' }}>{trend.state} — {trend.source}</span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{trend.year}</span>
              </div>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                <strong>{analysis.analysis_type}:</strong> {trend.bact_limit} · <strong>Control:</strong> {trend.control}
              </div>
              {trend.notes && <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>{trend.notes}</div>}
            </div>
          ))}
        </Section>
      )}

      {/* Permit Application Guidance */}
      {analysis.permit_application_guidance && (
        <Section title="Permit Application Guidance" icon={<ClipboardList size={15} color="#D97706" />} defaultOpen={false}>
          {analysis.permit_application_guidance.key_analyses_needed && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Key Analyses to Include</div>
              {analysis.permit_application_guidance.key_analyses_needed.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#0066FF', marginTop: 6, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: '#374151' }}>{item}</div>
                </div>
              ))}
            </div>
          )}
          {analysis.permit_application_guidance.agency_considerations && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>State-Specific Considerations</div>
              <div style={{ fontSize: 12, color: '#374151' }}>{analysis.permit_application_guidance.agency_considerations}</div>
            </div>
          )}
          {analysis.permit_application_guidance.common_pitfalls && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', marginBottom: 6 }}>Common Pitfalls to Avoid</div>
              {analysis.permit_application_guidance.common_pitfalls.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  <XCircle size={12} color="#DC2626" style={{ flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 12, color: '#374151' }}>{p}</div>
                </div>
              ))}
            </div>
          )}
          {analysis.permit_application_guidance.timeline && (
            <div style={{ fontSize: 13, color: '#64748b' }}><strong>Typical Timeline:</strong> {analysis.permit_application_guidance.timeline}</div>
          )}
        </Section>
      )}

      {/* Next Steps */}
      {analysis.next_steps && analysis.next_steps.length > 0 && (
        <Section title="Next Steps" icon={<ArrowRight size={15} color="#0066FF" />} defaultOpen={false}>
          {analysis.next_steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#0066FF', flexShrink: 0 }}>{i + 1}</div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, paddingTop: 4 }}>{step}</div>
            </div>
          ))}
        </Section>
      )}

      {/* RBLC Search Guidance */}
      {analysis.rblc_search_guidance && (
        <Section title="Search RBLC Database" icon={<ExternalLink size={15} color="#0066FF" />} defaultOpen={false}>
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>{analysis.rblc_search_guidance.search_tips}</div>
            <div className="tool-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12, marginBottom: 12 }}>
              <div><span style={{ color: '#64748b' }}>Process Codes: </span><strong>{analysis.rblc_search_guidance.key_process_codes}</strong></div>
              <div><span style={{ color: '#64748b' }}>Date Range: </span><strong>{analysis.rblc_search_guidance.recommended_date_range}</strong></div>
            </div>
            <a href={analysis.rblc_search_guidance.rblc_url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0066FF', color: 'white', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              <ExternalLink size={13} /> Open RBLC Database on EPA.gov
            </a>
          </div>
        </Section>
      )}

      {/* Disclaimer */}
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
        <div style={{ fontSize: 11, color: '#DC2626', lineHeight: 1.6, display: 'flex', gap: 6 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          {analysis.disclaimer}
        </div>
      </div>
    </div>
  );
}

export default function BactLaer() {
  const [facilities, setFacilities] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeResult, setActiveResult] = useState(null);
  const [activeRecordId, setActiveRecordId] = useState(null);

  const [form, setForm] = useState({
    facilityId: '',
    sourceType: '',
    customSourceType: '',
    pollutant: 'NOx',
    state: '',
    capacity: '',
    capacityUnit: 'MMBtu/hr',
    fuelType: '',
    analysisType: 'BACT',
    attainmentStatus: 'Attainment',
    additionalContext: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [facilitiesRes, analysesRes] = await Promise.all([
        facilityAPI.getAll(),
        api.get('/bact-laer')
      ]);
      setFacilities(facilitiesRes.data.facilities || []);
      setAnalyses(analysesRes.data.analyses || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleAnalyze = async () => {
    const sourceType = form.sourceType === 'Other (describe below)' ? form.customSourceType : form.sourceType;
    if (!sourceType) return toast.error('Select a source type');
    if (!form.state) return toast.error('Select a state');
    if (!form.pollutant) return toast.error('Select a pollutant');

    setAnalyzing(true);
    setActiveResult(null);
    try {
      const res = await api.post('/bact-laer/analyze', {
        facilityId: form.facilityId || null,
        sourceType,
        pollutant: form.pollutant,
        state: form.state,
        capacity: form.capacity,
        capacityUnit: form.capacityUnit,
        fuelType: form.fuelType,
        analysisType: form.analysisType,
        attainmentStatus: form.attainmentStatus,
        additionalContext: form.additionalContext
      });
      setActiveResult(res.data.analysis);
      setActiveRecordId(res.data.record.id);
      setAnalyses(prev => [res.data.record, ...prev]);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Analysis failed — try again');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this analysis?')) return;
    try {
      await api.delete(`/bact-laer/${id}`);
      setAnalyses(prev => prev.filter(a => a.id !== id));
      if (activeRecordId === id) { setActiveResult(null); setActiveRecordId(null); }
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', background: 'white', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#64748b' }}>Loading...</div>;

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ marginBottom: 24 }}>
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FlaskConical size={22} color="#0066FF" />
          BACT / LAER / RACT Analysis
        </div>
        <div className="page-subtitle">
          {form.analysisType === 'RACT'
            ? form.state === 'Pennsylvania'
              ? 'AI-guided RACT II and RACT III analysis for existing sources in Pennsylvania non-attainment areas'
              : 'AI-guided Reasonably Available Control Technology analysis for existing sources in non-attainment areas'
            : form.analysisType === 'LAER'
            ? 'AI-guided Lowest Achievable Emission Rate analysis for new/modified major sources in non-attainment areas'
            : 'AI-guided Best Available Control Technology analysis for new/modified major sources in PSD attainment areas'}
        </div>
      </div>

      <div className="tool-layout" style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24, alignItems: 'start' }}>

        {/* LEFT — Form + History */}
        <div>
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 20 }}>
              New Analysis
            </div>

            {/* Analysis Type */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Analysis Type *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {[
                  { type: 'BACT', sub: 'Attainment / PSD' },
                  { type: 'LAER', sub: 'Non-Attainment' },
                  { type: 'RACT', sub: 'Existing Source' },
                ].map(({ type, sub }) => (
                  <button key={type} onClick={() => {
                    setField('analysisType', type);
                    if (type === 'RACT') setField('attainmentStatus', 'Non-attainment');
                    if (type === 'BACT') setField('attainmentStatus', 'Attainment');
                    if (type === 'LAER') setField('attainmentStatus', 'Non-attainment');
                  }}
                    style={{ padding: '10px 6px', border: `2px solid ${form.analysisType === type ? '#0066FF' : '#E2E8F0'}`, borderRadius: 8, background: form.analysisType === type ? '#EFF6FF' : 'white', color: form.analysisType === type ? '#0066FF' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                    {type}
                    <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, color: form.analysisType === type ? '#3B82F6' : '#94a3b8' }}>
                      {sub}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* PA RACT II/III banner */}
            {form.analysisType === 'RACT' && form.state === 'Pennsylvania' && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>Pennsylvania RACT II / RACT III</div>
                <div style={{ fontSize: 11, color: '#3B82F6', lineHeight: 1.55 }}>
                  PA has two active RACT programs. RACT II (2015, 100+ tpy) and RACT III (2023, 50+ tpy). Analysis will address both and identify which applies to this source.
                </div>
              </div>
            )}

            {/* Attainment Status */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Attainment Status *</label>
              <select style={inputStyle} value={form.attainmentStatus} onChange={e => setField('attainmentStatus', e.target.value)}>
                {ATTAINMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Facility */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Facility (optional)</label>
              <select style={inputStyle} value={form.facilityId} onChange={e => setField('facilityId', e.target.value)}>
                <option value="">Select facility...</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
              </select>
            </div>

            {/* State */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>State *</label>
              <select style={inputStyle} value={form.state} onChange={e => setField('state', e.target.value)}>
                <option value="">Select state...</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Source Type */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Source Type *</label>
              <select style={inputStyle} value={form.sourceType} onChange={e => setField('sourceType', e.target.value)}>
                <option value="">Select source type...</option>
                {SOURCE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {form.sourceType === 'Other (describe below)' && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Describe Source *</label>
                <input style={inputStyle} placeholder="e.g. Asphalt drum mix plant, 500 tph" value={form.customSourceType} onChange={e => setField('customSourceType', e.target.value)} />
              </div>
            )}

            {/* Pollutant */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Pollutant *</label>
              <select style={inputStyle} value={form.pollutant} onChange={e => setField('pollutant', e.target.value)}>
                {POLLUTANTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Capacity */}
            <div className="tool-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Capacity / Size</label>
                <input style={inputStyle} type="number" placeholder="e.g. 250" value={form.capacity} onChange={e => setField('capacity', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Unit</label>
                <select style={inputStyle} value={form.capacityUnit} onChange={e => setField('capacityUnit', e.target.value)}>
                  {['MMBtu/hr', 'MW', 'HP', 'tph', 'ton/yr', 'gal/hr', 'scfm', 'bbl/day'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* Fuel */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Fuel Type</label>
              <input style={inputStyle} placeholder="e.g. Natural Gas, Diesel, Coal" value={form.fuelType} onChange={e => setField('fuelType', e.target.value)} />
            </div>

            {/* Additional Context */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Additional Context (optional)</label>
              <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }}
                placeholder="e.g. New construction, greenfield site, existing facility modification, specific control technology already proposed..."
                value={form.additionalContext}
                onChange={e => setField('additionalContext', e.target.value)} />
            </div>

            <button onClick={handleAnalyze}
              disabled={analyzing || !form.sourceType || !form.state || !form.pollutant}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: analyzing || !form.sourceType || !form.state || !form.pollutant ? '#94a3b8' : '#0066FF',
                color: 'white', border: 'none', borderRadius: 9, padding: '12px', fontSize: 14, fontWeight: 600,
                cursor: analyzing || !form.sourceType || !form.state || !form.pollutant ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)' }}>
              {analyzing
                ? <><Loader size={15} style={{ animation: 'spin 0.8s linear infinite' }} />Running Analysis...</>
                : <><FlaskConical size={15} />Run {form.analysisType}{form.analysisType === 'RACT' && form.state === 'Pennsylvania' ? ' II/III' : ''} Analysis</>}
            </button>

            {analyzing && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
                Running top-down {form.analysisType} analysis...<br />This may take 20-30 seconds.
              </div>
            )}
          </div>

          {/* History */}
          {analyses.length > 0 && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>Previous Analyses</div>
              </div>
              {analyses.map(a => {
                const isActive = activeRecordId === a.id;
                return (
                  <div key={a.id} onClick={() => {
                    const result = typeof a.analysis_result === 'string' ? JSON.parse(a.analysis_result) : a.analysis_result;
                    setActiveResult(result);
                    setActiveRecordId(a.id);
                  }} style={{ padding: '12px 20px', borderBottom: '1px solid #F8FAFC', cursor: 'pointer', background: isActive ? '#EFF6FF' : 'white', borderLeft: isActive ? '3px solid #0066FF' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{a.source_type}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {a.pollutant} · {a.state} · {a.analysis_type}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                          {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ background: a.analysis_type === 'LAER' ? '#FEF2F2' : '#EFF6FF', color: a.analysis_type === 'LAER' ? '#DC2626' : '#0066FF', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8 }}>
                          {a.analysis_type}
                        </span>
                        <button onClick={e => { e.stopPropagation(); handleDelete(a.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — Results */}
        <div>
          {analyzing && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <Loader size={36} color="#0066FF" style={{ animation: 'spin 0.8s linear infinite', marginBottom: 20 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>
                Running {form.analysisType} Analysis
              </div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 340, margin: '0 auto', lineHeight: 1.65 }}>
                Conducting top-down {form.analysisType} analysis for {form.pollutant} emissions from {form.sourceType || 'your source'}. Evaluating control technologies, technical feasibility, economic impacts, and RBLC trends.
              </div>
            </div>
          )}

          {!analyzing && activeResult && (
            <AnalysisResults
              analysis={activeResult}
              recordId={activeRecordId}
              onDelete={() => handleDelete(activeRecordId)}
            />
          )}

          {!analyzing && !activeResult && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <FlaskConical size={52} color="#E2E8F0" style={{ marginBottom: 20 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 10 }}>
                BACT / LAER Analysis
              </div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 400, margin: '0 auto', lineHeight: 1.7 }}>
                Select your source type, pollutant, and state. Choose BACT for new sources in attainment areas, LAER for new sources in non-attainment areas, or RACT for existing sources. Pennsylvania users get RACT II and RACT III analysis automatically.
              </div>
              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                {['Top-Down Analysis', 'RBLC Trends', 'Cost Effectiveness', 'Permit Guidance', 'Next Steps'].map(tag => (
                  <span key={tag} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: '#64748b' }}>{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
